import React, { useCallback, useState, useRef, forwardRef, useEffect } from "react";
import styles from "./CardFront.module.css";
import { combineClasses } from "../../../helpers/miscellaneous";

interface CardFrontProps {
  imageUrl?: string;
  height?: string;
  onImageLoad?: (dimensions: { width: number; height: number }) => void;
}

const maxLargeDimension = 3;
const maxSmallDimension = 2;

// Portrait and landscape limits based on dimension constants
const MIN_ASPECT_RATIO = maxSmallDimension / maxLargeDimension;
const MAX_ASPECT_RATIO = maxLargeDimension / maxSmallDimension;

const PlaceholderSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="108"
    height="128"
    viewBox="0 0 108 128"
    fill="none">
    <rect width="108" height="128" fill="#D9D9D9" />
  </svg>
);

export const CardFront = forwardRef<HTMLDivElement, CardFrontProps>(
  ({ imageUrl, height = "100%", onImageLoad }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [aspectRatioStyle, setAspectRatioStyle] = useState<string | undefined>();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const handleImageLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        const dimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight
        };

        const aspectRatio = dimensions.width / dimensions.height;

        // If image exceeds max landscape ratio, constrain to max ratio
        if (aspectRatio > MAX_ASPECT_RATIO) {
          setAspectRatioStyle(`${maxLargeDimension} / ${maxSmallDimension}`);
        }
        // If image exceeds max portrait ratio, constrain to max ratio
        else if (aspectRatio < MIN_ASPECT_RATIO) {
          setAspectRatioStyle(`${maxSmallDimension} / ${maxLargeDimension}`);
        }
        // Use the image's natural aspect ratio
        else {
          setAspectRatioStyle(`${dimensions.width} / ${dimensions.height}`);
        }

        onImageLoad?.(dimensions);
        setIsVisible(true);
      },
      [onImageLoad]
    );

    useEffect(() => {
      if (!imageUrl) {
        setIsVisible(true);
      }
    }, [imageUrl]);

    return (
      <div
        ref={(node) => {
          wrapperRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={combineClasses(styles.wrapper, isVisible && styles.visible)}
        style={{ height }}>
        <div className={styles.imageContainer}>
          {imageUrl ? (
            <div
              className={styles.innerWrapper}
              style={aspectRatioStyle ? { aspectRatio: aspectRatioStyle } : undefined}>
              <img
                ref={imageRef}
                className={styles.photo}
                src={imageUrl}
                alt="Uploaded photo"
                onLoad={handleImageLoad}
              />
            </div>
          ) : (
            <div className={styles.placeholder}>
              <PlaceholderSVG />
            </div>
          )}
        </div>
        <img
          className={styles.logo}
          src="https://wvusstatic.com/sak/questionnaire/wv-logo_on_card.svg"
          alt="WV Logo"
        />
      </div>
    );
  }
);
